import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { from, map } from 'rxjs';
import * as _ from 'lodash';
import { LoggerService } from '../services/logger.service';
import { Storage } from '../storage';
import { LayoutToastService } from '../services/layout-toast.service';
import { RoleMatch } from '../enums/role-match.enum';
import { AUTH_SESSION } from '@shared/di/auth-session.token';
import { SUPER_ADMIN_PERMISSION } from '../constants/auth';

function getJSON(key: string): object | null {
  const item = localStorage.getItem(key);
  if (item === null) return null;
  try {
    return JSON.parse(item);
  } catch (e) {
    return null;
  }
}

/**
 * Define a dynamic role guard, granting access according to the provided
 * matching strategy
 * @param route
 * @param state
 */
export const roleGuardFn: CanActivateFn = (route, state) => {
  const logger = inject(LoggerService);
  const toastService = inject(LayoutToastService);
  const session = inject(AUTH_SESSION);

  let options = {type: null, names: [], mode: RoleMatch.ANY, redirect: false};

  try {
    options = {...options, ...route.data['guards']['role']};
    if (!('type' in options)) return true;
  } catch {
    logger.warn('RuleGuard', 'No options found');
    return true;
  } finally {
    logger.debug(`RoleGuard options: ${JSON.stringify(options)}`);
  }

  // Extract current user permissions or authorities
  const grants = session.readStoredGrants();
  if (!grants) {
    logger.error('roleGuard', 'User not found. Blocking navigation');
    // The UI is not synced. Clean localStorage
    Storage.clear();
    return false;
  }

  const roles: string[] = [];
  if (options['type'] === 'authorities') {
    roles.push(...grants.authorityNames);
  } else if (options['type'] === 'permissions') {
    roles.push(...grants.permissionNames);
  } else {
    logger.error('roleGuard', 'type', options['type'], 'not allowed. Blocking navigation');
    toastService.showError('ERROR: Not allowed');
    return false;
  }

  if (roles.includes(SUPER_ADMIN_PERMISSION)) return true;

  const { names, mode } = options;

  logger.debug('RoleGuard', mode, roles, names);
  let isAllowed = true;

  switch (mode) {
    case RoleMatch.ANY:
      isAllowed = _.intersection(roles, names).length !== 0;
      break;
    case RoleMatch.ALL:
      isAllowed = _.intersection(roles, names).length === names.length;
      break;
    case RoleMatch.NONE:
      isAllowed = _.difference(roles, names).length === roles.length;
      break;
  }

  if (isAllowed) return true;
  if (!options['redirect']) {
    toastService.showError($localize`Non hai i permessi necessari per accedere a questa pagina`);
    return false;
  }

  return from(inject(Router).navigateByUrl('/')).pipe(
    map(() => false)
  );
};
